import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getData, getcountries, getSeriesType } from '../../../services/admin/invitationLetter/invitationLetterServices';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

const subDomain = localStorage.getItem('sub_domain') || '';

const formatSeries = (series) => {
  if (!series) return '';
  const date = new Date(series);
  if (isNaN(date.getTime())) return series;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const CurrentInvitationLetter = () => {
  const [dataList, setDataList] = useState([]);
  const [activeSeriesList, setActiveSeriesList] = useState([]); // multiple active series supported
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [dropdownData, setDropdownData] = useState({ countries: [] });

  const navigate = useNavigate();

  // Resolve ALL currently active series from esSeriesType (is_active === 'active').
  // More than one row can be active at the same time.
  const fetchActiveSeries = useCallback(async () => {
    try {
      const seriesType = await getSeriesType();
      const activeSeries = (seriesType || [])
        .filter((s) => s.is_active === 'active')
        .map((s) => s.series);
      return activeSeries;
    } catch (error) {
      console.error('Failed to fetch active series:', error);
      return [];
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ sub_domain: subDomain });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const countries = await getcountries();
      setDropdownData(prev => ({ ...prev, countries: countries || [] }));
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const seriesList = await fetchActiveSeries();
      setActiveSeriesList(seriesList);
      await Promise.all([fetchData(), fetchDropdownData()]);
      setLoading(false);
    };
    init();
  }, [fetchActiveSeries, fetchData, fetchDropdownData]);

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange = (page) => setCurrentPage(page);

  const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const handleView = (row) => {
    navigate('/invitationLetterPdf', {
      state: { data: { data: row }, viewOnly: true }
    });
  };

  const filteredData = useMemo(() => {
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;

    return dataList
      .filter((data) => {
        const matchesText =
          data.speaker_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          data.invitation_id?.toLowerCase().includes(searchText.toLowerCase());

        const date = data.createdAt ? new Date(data.createdAt) : null;
        const inRange = (startDateObj && endDateObj)
          ? date >= startDateObj && date <= endDateObj
          : true;

        const matchCountry = !countryFilter || data.country === countryFilter;

        // Row's series must be one of the currently active series.
        const matchesActiveSeries =
          activeSeriesList.length === 0 || activeSeriesList.includes(data.series);

        return matchesText && inRange && matchCountry && matchesActiveSeries;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [dataList, searchText, startDate, endDate, countryFilter, activeSeriesList]);

  const columns = [
    { name: '#', selector: (row, i) => (currentPage - 1) * 10 + (i + 1), sortable: false, width: '60px' },
    { name: 'Invitation ID', selector: (row) => row.invitation_id, sortable: true, wrap: true },
    { name: 'Series', selector: (row) => formatSeries(row.series), sortable: true, wrap: true },
    { name: 'Speaker / Delegate Name', selector: (row) => row.speaker_name, sortable: true, wrap: true },
    { name: 'Category', selector: (row) => row.invitation_category, sortable: true, wrap: true },
    { name: 'Abstract Type', selector: (row) => row.abstract_type, sortable: true, wrap: true },
    { name: 'Country', selector: (row) => getCountryName(row.country), sortable: true, wrap: true },
    {
      name: 'Actions',
      cell: (row) => (
        <button className="btn btn-info btn-sm" onClick={() => handleView(row)}>
          View
        </button>
      ),
      width: '100px',
    },
  ];

  const ExpandableRow = ({ data }) => (
    <div className="p-3 bg-light border">
      <table className="table table-bordered mb-0">
        <tbody>
          <tr><td><b>Affiliation:</b></td><td>{data.organization || '-'}</td></tr>
          <tr><td><b>Email:</b></td><td>{data.email || '-'}</td></tr>
          <tr><td><b>Event Title:</b></td><td>{data.event_title || '-'}</td></tr>
          <tr><td><b>Event Date:</b></td><td>{data.event_date || '-'}</td></tr>
          <tr><td><b>Venue:</b></td><td>{data.venue || '-'}</td></tr>
          <tr><td><b>Presentation Title:</b></td><td>{data.abstract_title || '-'}</td></tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        <div className="row m-3 d-flex flex-column gap-3">
          <div className="d-flex gap-2">
            <div className="col-md-2">
              <span className="fs-5 text-dark">Countries</span>
              <select className="form-control" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                <option value="">All Countries</option>
                {dropdownData.countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <span className="fs-5 text-dark">Start Date</span>
              <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <span className="fs-5 text-dark">End Date</span>
              <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4 text-dark">
                    Letter Generation / Current Invitation Letter
                    {activeSeriesList.length > 0 && (
                      <span className="badge bg-info ms-2" style={{ fontSize: '0.75rem' }}>
                        Active Series: {activeSeriesList.map(formatSeries).join(', ')}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 200, 500]}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <input className="form-control" type="text" placeholder="Search"
                    value={searchText} onChange={handleSearchChange} />
                }
                expandableRows
                expandableRowsComponent={ExpandableRow}
              />
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentInvitationLetter;